import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { border_radius_styles_with_size, semantic_colors } from '../../foundation';
import { CSSValueWithLength } from '../../styles';
import { IconCircleXFill } from '../icons/generated';
import { getImagePreviewSize, ImagePreview } from '../image';

export interface ImageUploadPreviewProps {
  src?: string;
  /** @default 100 */
  width?: number;
  /** @default 100 */
  height?: number;
  file?: File;
  alt?: string;
  label?: string;
  objectFit: 'contain' | 'cover' | 'fill';
  /** @default 'medium' */
  size?: 'medium' | 'small';
  readOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
  backgroundDark?: boolean;
  removable?: boolean;
  status?: 'error';
  /** @default border_radius_styles_with_size.small */
  radius?: CSSValueWithLength;
  onClick?: (image_url: string) => void;
  onRemove?: () => void;
}

export const ImageUploadPreview = ({
  size = 'medium',
  width = 100,
  height = 100,
  objectFit,
  src,
  file,
  alt,
  label,
  readOnly,
  disabled,
  loading,
  backgroundDark,
  status,
  removable,
  radius = border_radius_styles_with_size.small,
  onClick,
  onRemove,
}: ImageUploadPreviewProps) => {
  const [file_src, setFileSrc] = React.useState<string>();
  const fileReader = new FileReader();
  file && fileReader.readAsDataURL(file);

  useEffect(() => {
    fileReader.onload = (e) => {
      const src = e.target?.result;
      setFileSrc(src as string);
    };
    return () => {
      fileReader.onload = null;
    };
  }, []);

  return (
    <Container size={size} width={width} height={height} disabled={disabled}>
      <ImagePreview
        src={src || file_src}
        alt={alt || ''}
        width={width}
        height={height}
        size={size}
        label={label}
        status={status}
        backgroundDark={backgroundDark}
        objectFit={objectFit}
        onClick={() => onClick?.(src || file_src || '')}
        radius={radius}
      />
      {removable !== false && !readOnly && !disabled && !loading && (
        <DeleteButton
          size={size}
          type='button'
          disabled={disabled}
          onClick={() => {
            onRemove?.();
          }}
        >
          <IconCircleXFill size={size === 'medium' ? 24 : 16} color={semantic_colors.content.primary} />
        </DeleteButton>
      )}
    </Container>
  );
};

const DeleteButton = styled.button<{ disabled?: boolean; size: 'medium' | 'small' }>`
  position: absolute;
  top: ${({ size }) => (size === 'medium' ? '4px' : '-3px')};
  right: ${({ size }) => (size === 'medium' ? '4px' : '-3px')};
  cursor: pointer;
  padding: 0;
  border: none;
  background: initial;

  ${({ disabled }) => disabled && 'cursor: not-allowed;'}
`;

const Container = styled.div<{
  size: 'medium' | 'small';
  width: number;
  height: number;
  disabled?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ size, width, height }) => getImagePreviewSize({ size, width, height })};
  ${({ disabled }) => disabled && 'opacity: 0.4;'}
`;
